import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { apiHost } from '../../apiConfig';
import { useLiveList } from '../../SocketContext';
import Icon from '../ui/Icon';
import { Marking } from '../ui/primitives';
import ThemeToggle from '../ui/ThemeToggle';
import { useMedia } from '../ui/useMedia';
import { experienceLabel, isExperience } from '../../accountFacts';

/**
 * AppShell — top bar + sidebar + scrolling main.
 * Sidebar: 56 px rows, current page = 3 px accent bar + accent-subtle fill.
 * Auto-rails below 1100 px (user choice remembered), drawer below 768 px.
 * Title block: the portal's own marking (which portal, on which host) at the sidebar's foot, hidden in the
 * icon rail, and in the drawer's foot on a phone.
 */
const RAIL_KEY = 'enablegames_sidebar_rail';

const PROVIDER_NAV = [
    { key: 'profile', label: 'Profile', icon: 'profile', to: '/provider-profile', owns: ['/provider-profile'] },
    { key: 'players', label: 'Players', icon: 'players', to: '/players', owns: ['/players'] },
    { key: 'live', label: 'Live', icon: 'live', to: '/live', owns: ['/live'] },
    { key: 'sessions', label: 'Sessions', icon: 'sessions', to: '/sessions', owns: ['/sessions'] },
    { key: 'add', label: 'Add player', icon: 'addPlayer', to: '/sponsored-player', owns: ['/sponsored-player'] },
];
const PLAYER_NAV = [
    { key: 'profile', label: 'Profile', icon: 'profile', to: '/player-profile', owns: ['/player-profile'] },
    { key: 'live', label: 'Live', icon: 'live', to: '/live', owns: ['/live'] },
    { key: 'sessions', label: 'Sessions', icon: 'sessions', to: '/sessions', owns: ['/sessions'] },
];

function readRail() {
    try {
        const v = localStorage.getItem(RAIL_KEY);
        return v === '1' ? true : v === '0' ? false : null;
    } catch {
        return null;
    }
}

export default function AppShell({ children }) {
    const { user, logout } = useAuth();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const mobile = useMedia('(max-width: 767px)');
    const narrow = useMedia('(max-width: 1099px)');
    const [railPref, setRailPref] = useState(readRail);
    const [drawer, setDrawer] = useState(false);
    const mainRef = useRef(null);
    const drawerCloseRef = useRef(null);
    const live = useLiveList();

    const role = user?.role === 'player' ? 'player' : 'provider';
    const nav = role === 'provider' ? PROVIDER_NAV : PLAYER_NAV;
    const railed = !mobile && (railPref ?? narrow);
    const liveCount = role === 'player' ? live.sessions.filter((s) => s.userId === user?.user_id).length : live.sessions.length;
    const name = user?.name || user?.email || 'Signed in';
    const initial = name.trim().charAt(0).toUpperCase() || '?';
    const roleLine = isExperience(user) ? experienceLabel(user.experienceEndsAt) : role === 'provider' ? 'Provider' : 'Player';
    const titleBlock = (className) => (
        <Marking className={className} name={role === 'provider' ? 'Provider portal' : 'Player portal'} lines={[apiHost()]} />
    );

    // New page: scroll the main column to the top and close the drawer.
    useEffect(() => {
        if (mainRef.current) mainRef.current.scrollTop = 0;
        setDrawer(false);
    }, [pathname]);

    useEffect(() => {
        if (!drawer) return undefined;
        drawerCloseRef.current?.focus();
        const onKey = (e) => { if (e.key === 'Escape') setDrawer(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawer]);

    const toggleRail = () => {
        const next = !railed;
        setRailPref(next);
        try { localStorage.setItem(RAIL_KEY, next ? '1' : '0'); } catch { /* ignore */ }
    };

    const onLogout = () => {
        logout();
        navigate('/login');
    };

    const isCurrent = (item) => item.owns.some((p) => pathname === p || pathname.startsWith(p + '/'));

    const navItems = (labels) => nav.map((item) => {
        const current = isCurrent(item);
        const showLive = item.key === 'live' && liveCount > 0;
        return (
            <Link key={item.key} to={item.to} className="eg-nav__item" aria-current={current ? 'page' : undefined} title={labels ? undefined : item.label}>
                <Icon name={item.icon} />
                {showLive ? <span className="eg-nav__livedot" aria-hidden="true" /> : null}
                {labels ? <span>{item.label}</span> : <span className="eg-sr-only">{item.label}</span>}
                {showLive && labels ? <span className="eg-nav__livetext">In session</span> : null}
                {showLive && !labels ? <span className="eg-sr-only">, in session</span> : null}
            </Link>
        );
    });

    return (
        <div className="eg-shell">
            <header className="eg-topbar">
                {mobile ? (
                    <button type="button" className="eg-btn eg-btn--icon" aria-label="Open navigation" aria-expanded={drawer} onClick={() => setDrawer(true)}>
                        <Icon name="menu" />
                    </button>
                ) : null}
                <Link to={nav[0].to} className="eg-brand" aria-label="enAble Games — home">
                    <span className="eg-wordmark">ENABLE</span>
                    {!mobile ? (
                        <>
                            <span className="eg-vrule" />
                            <span className="eg-portal-label">{role === 'provider' ? 'Provider portal' : 'Player portal'}</span>
                        </>
                    ) : null}
                </Link>
                <span className="eg-spacer" />
                <div className="eg-user">
                    <span className="eg-avatar" aria-hidden="true">{initial}</span>
                    {!mobile ? (
                        <div className="eg-user__text">
                            <span className="eg-user__name">{name}</span>
                            <span className="eg-user__role">{roleLine}</span>
                        </div>
                    ) : null}
                </div>
                {!mobile ? (
                    <>
                        <span className="eg-vrule" />
                        <ThemeToggle />
                    </>
                ) : null}
                <span className="eg-vrule" />
                <button type="button" className="eg-logout" onClick={onLogout} aria-label="Log out">
                    <Icon name="logout" />
                    {!mobile ? <span>Log out</span> : null}
                </button>
            </header>

            <div className="eg-shell__body">
                {!mobile ? (
                    <nav aria-label="Main" className={railed ? 'eg-nav eg-nav--rail' : 'eg-nav'}>
                        {navItems(!railed)}
                        <span className="eg-spacer" />
                        {!railed ? titleBlock('eg-nav__title') : null}
                        <div className="eg-nav__rule" />
                        <button type="button" className="eg-nav__item eg-nav__collapse" onClick={toggleRail} aria-label={railed ? 'Expand sidebar' : 'Collapse sidebar'} title={railed ? 'Expand sidebar' : 'Collapse sidebar'}>
                            <Icon name="chevronLeft" />
                            {!railed ? <span>Collapse</span> : null}
                        </button>
                    </nav>
                ) : null}
                <main ref={mainRef} className="eg-main" id="main">
                    {children}
                </main>
            </div>

            {mobile && drawer ? (
                <div className="eg-drawer">
                    <button type="button" className="eg-drawer__scrim" aria-label="Close navigation" tabIndex={-1} onClick={() => setDrawer(false)} />
                    <nav aria-label="Main" role="dialog" aria-modal="true" className="eg-drawer__panel">
                        <div className="eg-drawer__head">
                            <span className="eg-wordmark">ENABLE</span>
                            <span className="eg-spacer" />
                            <button ref={drawerCloseRef} type="button" className="eg-btn eg-btn--icon" aria-label="Close navigation" onClick={() => setDrawer(false)}>
                                <Icon name="close" />
                            </button>
                        </div>
                        <div className="eg-drawer__user">
                            <span className="eg-user__name">{name}</span>
                            <span className="eg-user__role">{roleLine}</span>
                        </div>
                        <div className="eg-drawer__list">{navItems(true)}</div>
                        <span className="eg-spacer" />
                        <div className="eg-drawer__foot">{titleBlock('eg-drawer__title')}<ThemeToggle /></div>
                    </nav>
                </div>
            ) : null}
        </div>
    );
}
